import { Component, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { instanceToInstance } from 'class-transformer';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';

import { Account } from '../../../../../auth/models/account.model';
import { User } from '../../../../../auth/models/user.model';
import { LoginService } from '../../../../../auth/services/login.service';
import { Company, companyIs } from '../../../../../models/company.model';
import { Product } from '../../../../../models/product.model';
import { Slot } from '../../../../../models/slot.model';
import { CPEActors, Destination, ElectronicWaybill, SlotsBatch } from '../../../../../models/slots-batch.model';
import { Zone } from '../../../../../models/zone.model';
import { CompanyService } from '../../../../../services/company.service';
import { DataDogLoggerService } from '../../../../../services/data-dog-logger.service';
import { DestinationsService } from '../../../../../services/destinations.service';
import { IntercomService } from '../../../../../services/intercom.service';
import { LocationService } from '../../../../../services/location.service';
import { SlotService } from '../../../../../services/slot.service';
import { FormReuseService } from '../../../../../ui/services/form-reuse.service';
import { SlotsRequest } from '../../../../models/slots-request.model';
import { SlotRequestService } from '../../../../services/slot-request.service';
import { endOfDay, startOfDay } from '../../../../../utilities/date';
import { Negotiation } from '../../../commercial/models/negotiation.model';
import { Contract } from '../../models/contract.model';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'ag-contracts-slots-modals',
  templateUrl: './contracts-slots-modals.component.html',
  styleUrls: ['./contracts-slots-modals.component.scss']
})
export class ContractsSlotsModals implements OnDestroy {

  @ViewChild('assignToContractModal', { static: true }) private readonly assignToContractModal: TemplateRef<any>;
  @ViewChild('assignSlotsModal', { static: true }) private readonly assignSlotsModal: TemplateRef<any>;
  @ViewChild('acceptSlotsModal', { static: true }) private readonly acceptSlotsModal: TemplateRef<any>;
  @ViewChild('rejectModal', { static: true }) private readonly rejectModal: TemplateRef<any>;
  @ViewChild('requestSlotsModal', { static: true }) private readonly requestSlotsModal: TemplateRef<any>;

  @Input() public company: Company;
  @Input() private user: User;
  @Input() public contract: Contract
  @Input() public details: boolean = true;

  private modalRef: BsModalRef[];
  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  public selectedBatch: SlotsBatch;
  public availableBatches: SlotsBatch[];
  public reasons: string;
  public slotRequest: SlotsRequest;
  public slotsToAssignSelectedQty: number;
  public availableCounterparts: Company[];
  public processing: boolean = false;
  public deliveryZones: Zone[];
  public destinations: Destination[] = [];
  public deliveryZoneSelected = null;
  public destinationSelected = null;
  public dateRange: Date[];

  /**
    * Enables a more simpler UX modal to accept a {@link SlotsBatch}.
    */
  public simplerAccept: boolean;
  /** Amount of accepted {@link Slot|Slots}. */
  public acceptedSlots: number = 0;
  public assignModel: {
    noMoreCompanies: boolean,
    assignedToMyCompany: boolean,
    assigned: SlotsBatch[],
    available: Slot[],
    observations?: string,
    reference: number,
    parent_id: number,
    actors?: any
  };

  public contractPendingSummary: { pending_count: number, pending_quantity: number } | null = null;

  constructor(
    private destinationsService: DestinationsService,
    private slotService: SlotService,
    private modalService: BsModalService,
    private formReuseService: FormReuseService,
    public companyService: CompanyService,
    private dataDogLoggerService: DataDogLoggerService,
    private locationService: LocationService,
    private intercomService: IntercomService,
    private loginService: LoginService,
    private slotRequestService: SlotRequestService
  ) {
    // Obtener el usuario si no se pasa como Input
    this.loginService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (!this.user && user) {
          this.user = user;
        }
      });
  }

  /**
   * Modal displaying methods
   * 
   */
  public assignBatchToContract(contract: Contract, slotRequest?: SlotsRequest): void {
    if (slotRequest) {
      this.slotRequest = slotRequest;
    }

    this.contract = contract;
    this.availableBatches = undefined;
    this.assignModel = undefined;
    this.contractPendingSummary = null;

    // Reset filters on every open to avoid stale state from previous modal use
    this.deliveryZoneSelected = null;
    this.destinationSelected = null;
    this.destinations = [];
    this.dateRange = undefined;

    if (contract?.id) {
      this.subscriptions.push(
        this.slotRequestService.summary(this.company.id, { 'filters[contract][id]': contract.id })
          .subscribe(summary => {
            this.contractPendingSummary = summary;
          })
      );
    }

    const filters: any = {
      product_id: contract.product.id,
      status_id: 2, // Only accepted batches
      validity: 'on_date'
    };

    // Load destination zones for the prefilter (when slotRequest has a destination)
    this.subscriptions.push(this.locationService.getZones().subscribe(deliveryZones => {
      this.deliveryZones = deliveryZones;
      this.deliveryZoneSelected = this.resolveZonePrefilter(deliveryZones, contract, slotRequest);
      
      // Refresh available batches if a zone was preselected
      if (this.deliveryZoneSelected) {
        this.refreshAvailableBatches(null);
      }
    }));

    // Keep destination selector options loaded (without auto-prefiltering destination).
    this.subscriptions.push(
      this.destinationsService.get(this.company.id).subscribe({
        next: destinations => {
          this.destinations = destinations || [];
        },
        error: () => {
          this.destinations = [];
        }
      })
    );

    this.subscriptions.push(this.slotService.watchBatches(this.company.id, filters).subscribe(response => {
      if (!response) return;

      this.availableBatches = response.body.filter(sb => {
        return sb.slots_unassigned > 0 && sb.company.id === this.company.id;
      });
    }));

    this.openModal(this.assignToContractModal, 'modal-lg');
  }

  private resolveZonePrefilter(deliveryZones: Zone[], contract?: Contract, slotRequest?: SlotsRequest): Zone | null {
    if (!deliveryZones?.length) return null;

    // Prioritize zone from slotRequest destination if available
    if (slotRequest?.destination?.name) {
      // Try exact match first
      const exactMatch = deliveryZones.find(zone => 
        this.normalizeText(zone.name) === this.normalizeText(slotRequest.destination.name)
      );
      if (exactMatch) {
        return exactMatch;
      }

      // Fallback to fuzzy match
      const fuzzyMatch = this.findBestByName(deliveryZones, [slotRequest.destination.name]);
      if (fuzzyMatch) {
        return fuzzyMatch;
      }
    }

    // Fallback to contract delivery zones
    const contractZones = (contract?.delivery?.locations || [])
      .map(location => location?.zone?.name || location?.location?.name)
      .filter(Boolean);

    if (contractZones.length === 0) {
      return null; // No zone to prefilter
    }

    const result = this.findBestByName(deliveryZones, contractZones);
    return result;
  }

  private findBestByName<T extends { name: string }>(options: T[], candidateNames: string[]): T | null {
    const normalizedCandidates = [...new Set(
      (candidateNames || [])
        .map(name => this.normalizeText(name))
        .filter(Boolean)
    )];

    if (!normalizedCandidates.length) return null;

    // Try exact match first
    for (const candidate of normalizedCandidates) {
      const exact = options.find(option => this.normalizeText(option?.name) === candidate);
      if (exact) {
        return exact;
      }
    }

    let bestMatch: T | null = null;
    let bestScore = 0;

    options.forEach(option => {
      const optionName = this.normalizeText(option?.name);
      if (!optionName) return;

      normalizedCandidates.forEach(candidate => {
        const regex = new RegExp(candidate.split(' ').map(part => this.escapeRegExp(part)).join('.*'));
        const tokenHits = candidate.split(' ').filter(token => token && optionName.includes(token)).length;
        const tokenScore = candidate.split(' ').length ? (tokenHits / candidate.split(' ').length) * 60 : 0;

        let score = tokenScore;
        if (regex.test(optionName)) score = Math.max(score, 80);
        if (optionName.includes(candidate) || candidate.includes(optionName)) score = Math.max(score, 85);

        if (score > bestScore) {
          bestScore = score;
          bestMatch = option;
        }
      });
    });

    return bestScore >= 40 ? bestMatch : null;
  }

  private normalizeText(value?: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  public openAcceptSlotsModal(batch: SlotsBatch, forceSimpler: boolean = false): void {
    this.simplerAccept = forceSimpler || companyIs(this.company, 'FARMER');
    this.acceptedSlots = batch.unassigned.length;
    this.execModal(this.acceptSlotsModal, batch);
  }

  /**
   * Accepts and assigns the entire {@link SlotsBatch} to the specified
   * {@link Company}.
   */
  public takeBatch(batch: SlotsBatch): void {
    this.processing = true;

    this.subscriptions.push(this.slotService.take(this.company.id, this.user.accounts.filter(this.filterAccount, this)[0], batch.id).subscribe(response => {
      this.doneModalProcessing();
    }));
  }

  public rejectBatch(batch: SlotsBatch): void {
    this.execModal(this.rejectModal, batch);
  }

  /**
   * Opens the modal to create one or more child {@link SlotsBatch} for the
   * specified batch.
   */
  public createBatch(parentBatch: SlotsBatch): void {
    this.assignModel = {
      noMoreCompanies: undefined,
      assignedToMyCompany: false, // TODO: Check if there is a batch assigned to my copmany
      assigned: [this.newSlotsBatch(instanceToInstance(parentBatch.unassigned), parentBatch.product, parentBatch.quality)],
      available: [],
      observations: '',
      reference: parentBatch.reference,
      parent_id: parentBatch.id
      // actors: instanceToInstance(parentBatch.actors), // Clone batch Actors
    };

    if (companyIs(this.company, 'FARMER')) this.assignToMyCompany(); // Farmers

    this.execModal(this.assignSlotsModal, parentBatch);
  }
  /**
   * Opens the modal to create one or more requests {@link SlotsBatch} for the
   * specified batch.
   */
  public requestBatchToContract(contract: Contract): void {

    this.contract = contract;
    this.availableBatches = undefined;

    // let filters = {
    //   product_id: contract.product.id,
    //   validity: 'on_date'
    // };

    // if (negotiation.proposal.business_detail.sustainable) filters['sustainable'] = true;

    // this.subscriptions.push(this.slotService.getBatches(this.company.id, filters).subscribe(response => {
    //   this.slotsSolicitadosBatches = response.body.filter(sb => {
    //     return sb.slots_unassigned > 0 && sb.company.id === this.company.id;
    //   });
    // }));

    this.openModal(this.requestSlotsModal, 'modal-lg');
  }

  public addBatch(): void {
    this.assignModel.assigned.push(this.newSlotsBatch(instanceToInstance(this.assignModel.available), this.selectedBatch.product, this.selectedBatch.quality));
    this.assignModel.available = [];
  }

  public removeBatch(batch_index: number): void {
    let sb: SlotsBatch = this.assignModel.assigned[batch_index],
      removedBatchSlots = sb.unassigned;

    if (sb.company.id === this.company.id) {
      this.assignModel.assignedToMyCompany = false;
    }

    this.assignModel.assigned.splice(batch_index, 1);
    this.assignModel.available = this.assignModel.available.concat(removedBatchSlots);
  }

  public isDisabledQuantity(index: number): boolean {
    const optionValue = index + 1;
    if (this.slotRequest) {
      return this.getSlotsQuantity() < optionValue;
    }
    return false;
  }

  public isSelectedQuantity(index: number): boolean {
    const slotsUnassigned = this.assignModel.assigned[0].slots_unassigned;
    const optionValue = index + 1;
    if (this.slotRequest?.quantity < slotsUnassigned) {
      // Set as max selection the quantity requested in slotRequest
      return this.slotRequest.quantity - this.slotRequest.assignedQuantity === optionValue;
    }

    return this.assignModel.assigned[0].slots_unassigned === optionValue;
  }

  public contractAssignableSlots(slots: Slot[], contract: Contract): Slot[] {
    if (!contract) {
      return slots;
    }

    return slots.filter((slots, index) => index < contract.assignableSlots)
  }

  public refreshAvailableBatches($event): void {
    const filters: any = {
      product_id: this.contract.product.id,
      validity: 'on_date',
    };

    if (this.dateRange && this.dateRange.length === 2) {
      const from = startOfDay(new Date(this.dateRange[0])).toISOString();
      const to = endOfDay(new Date(this.dateRange[1])).toISOString();
      filters['filters[date]'] = `between:${from},${to}`;
    }

    if (this.destinationSelected) {
      filters['destination_id'] = this.destinationSelected.id;
    }

    if (this.deliveryZoneSelected) {
      filters['zone_id'] = this.deliveryZoneSelected.id;
    }

    this.subscriptions.push(this.slotService.watchBatches(this.company.id, filters).subscribe(response => {
      if (!response) return;

      this.availableBatches = response.body.filter(sb => {
        return sb.slots_unassigned > 0 && sb.company.id === this.company.id;
      });
    }));
  }

  public clearDateRange(): void {
    this.dateRange = undefined;
    this.refreshAvailableBatches(null);
  }

  public getSlotsQuantity(): number {
    if (this.slotRequest) {
      return this.slotRequest.quantity - this.slotRequest.assignedQuantity;
    }
    return this.selectedBatch.unassigned.length
  }

  /** Fully or partially accepts the received {@link SlotsBatch}. */
  public acceptSlots(): void {
    this.processing = true;
    const status = {
      id: 2
    };

    if (this.acceptedSlots === this.selectedBatch.slots.length) {
      this.subscriptions.push(this.updateBatch(this.selectedBatch.id, status).subscribe(response => this.doneModalProcessing()));
    } else {
      let rejectedBatch = new SlotsBatch();

      rejectedBatch.status = {
        id: 3,
        reason: this.reasons
      }
      rejectedBatch.allocator = this.selectedBatch.allocator;
      rejectedBatch.company = this.selectedBatch.company;
      // rejectedBatch.negotiation = this.selectedBatch.negotiation;
      rejectedBatch.contract = this.selectedBatch.contract;
      rejectedBatch.parent_id = this.selectedBatch.parent_id;
      rejectedBatch.reference = this.selectedBatch.reference;
      rejectedBatch.date = this.selectedBatch.date;
      rejectedBatch.destination = this.selectedBatch.destination;
      rejectedBatch.product = this.selectedBatch.product;
      rejectedBatch.recipient = this.selectedBatch.recipient;

      let rejectedSlots = [];
      for (let index = this.acceptedSlots; index < this.selectedBatch.slots.length; index++) {
        rejectedSlots.push(this.selectedBatch.slots[index]);
      }

      rejectedBatch.unassigned = rejectedSlots;

      this.subscriptions.push(this.updateBatch(this.selectedBatch.id, status, rejectedBatch).subscribe(response => this.doneModalProcessing()));
    }
  }

  private updateBatch(batch_id: number, status: any, batch?: SlotsBatch): Observable<any> {
    return this.slotService.update(this.company.id, this.user.accounts.filter(this.filterAccount, this)[0], batch_id, status, batch);
  }

  /** Rejects the entire {@link SlotsBatch}. */
  public rejectSlots(): void {
    this.processing = true;

    this.subscriptions.push(this.updateBatch(this.selectedBatch.id, {
      id: 3,
      reason: this.reasons
    }).subscribe(response => this.doneModalProcessing()));
  }

  public changeAssignedQuantity(batch_index: number, e): void {
    const sb: SlotsBatch = this.assignModel.assigned[batch_index];
    const value = parseInt(e.target.value);

    this.slotsToAssignSelectedQty = value

    if (value < sb.unassigned.length) {
      // Remove slots
      const removedSlots = sb.unassigned.splice(-(sb.unassigned.length - value));
      this.assignModel.available = this.assignModel.available.concat(removedSlots);
    } else if (value > sb.unassigned.length) {
      // Add slots
      const slotsToAdd = this.assignModel.available.splice(0, (value - sb.unassigned.length));
      sb.unassigned = sb.unassigned.concat(slotsToAdd);
    }
  }

  public setBatchToContract(parentBatch: SlotsBatch): void {
    const sb = this.newSlotsBatch(instanceToInstance(parentBatch.unassigned), parentBatch.product, parentBatch.quality);
    sb.contract = this.contract;

    // Build counterparts: start with contract seller, add buyer (Rte. Comercial Venta Secundaria) and slot requester if different
    const counterparts: Company[] = [this.contract.seller];
    if (this.contract.buyer && !counterparts.some(c => c.id === this.contract.buyer.id)) {
      counterparts.push(this.contract.buyer);
    }
    const requesterCompany = this.slotRequest?.company;
    if (requesterCompany && !counterparts.some(c => c.id === requesterCompany.id)) {
      counterparts.push(requesterCompany);
    }
    this.availableCounterparts = counterparts;

    // Preload requester company when available; fallback to auto-select if there is only one option
    if (requesterCompany) {
      const preselectedCompany = this.availableCounterparts.find(c => c.id === requesterCompany.id);
      if (preselectedCompany) {
        sb.company = preselectedCompany;
      }
    } else if (this.availableCounterparts.length === 1) {
      sb.company = this.availableCounterparts[0];
    }

    this.assignModel = {
      noMoreCompanies: undefined,
      assignedToMyCompany: false, // TODO: Check if there is a batch assigned to my copmany
      assigned: [sb],
      available: [],
      observations: `Contrato <b>#${this.contract.reference}</b><br>`,
      reference: parentBatch.reference,
      parent_id: parentBatch.id
      // actors: instanceToInstance(parentBatch.actors), // Clone batch Actors
    };

    this.reasons = undefined;
    this.selectedBatch = parentBatch;

    // Obtengo los intervinientes del lote
    this.subscriptions.push(this.slotService.getActors(this.company.id, this.selectedBatch.id).subscribe(actors => {
      if (!this.selectedBatch.cpe) this.selectedBatch.cpe = new ElectronicWaybill();
      if (actors.destinatario) {
        this.selectedBatch.cpe.actors = new CPEActors(actors);
      } else {
        this.selectedBatch.cpe.actors = new CPEActors({
          destinatario: this.selectedBatch.recipient,
          destino: this.selectedBatch.destination.company
        });
      }
      // Asigno los intervinientes del contrato
      this.linkedListener(this.contract);
    }));
    this.setQuantityFromSlotRequest(0, this.slotRequest);
    this.slotsToAssignSelectedQty = this.assignModel.assigned[0].slots_unassigned;
  }

  public clearAssignedCompany(batch_index: number): void {
    if (this.assignModel.assigned.length === 1) {
      this.assignModel.assigned[batch_index].company = undefined;
      return;
    }

    this.removeBatch(batch_index);
  }

  private setQuantityFromSlotRequest(batch_index: number, slotRequest: SlotsRequest): void {
    if (slotRequest) {
      const avaiblesSlots = slotRequest.quantity - slotRequest.assignedQuantity;
      const sb: SlotsBatch = this.assignModel.assigned[batch_index];
      if (avaiblesSlots < sb.unassigned.length) {
        // Remove slots
        const removedSlots = sb.unassigned.splice(-(sb.unassigned.length - avaiblesSlots));
        this.assignModel.available = this.assignModel.available.concat(removedSlots);
      } else if (avaiblesSlots > sb.unassigned.length) {
        // Add slots
        const slotsToAdd = this.assignModel.available.splice(0, (avaiblesSlots - sb.unassigned.length));
        sb.unassigned = sb.unassigned.concat(slotsToAdd);
      }
    }
  }

  private newSlotsBatch(unassigned: Slot[], product?: Product, quality?: any): SlotsBatch {
    const nb = new SlotsBatch();

    nb.allocator = this.company;
    nb.unassigned = unassigned;
    if (quality) nb.quality = quality;

    if (product) nb.product = product;

    return nb;
  }

  public assignToMyCompany(): void {
    this.assignModel.assigned[this.assignModel.assigned.length - 1].company = this.company;
    this.assignModel.assignedToMyCompany = true;
  }

  /**
   * Save the data submitted to be reused.
   */
  private saveForm(callback?: Function): void {
    this.subscriptions.push(this.formReuseService.save(this.company.id, 'cpe-actors', this.selectedBatch.cpe.actors).subscribe({
      next: response => {
        if (callback) callback();
      },
      error: error => {
        // Non fatal error
        this.dataDogLoggerService.warn(error.message, error.error);
        if (callback) callback();
      }
    }));
  }

  private doneModalProcessing(): void {
    this.closeModal();
    this.processing = false;
  }

  public linkedListener(entityObj: Contract | Negotiation): void {
    // Assign actors from contract
    if (entityObj) {
      this.assignModel.observations += this.selectedBatch.cpe.inheritActors(entityObj);

      // Entregador para Syngenta
      this.selectedBatch.cpe.syngentaEntregador(entityObj as Contract, this.company, this.companyService.getCompaniesByFiscalValue(this.company.id, this.company.market.country_id, '30707386076'));
    }
  }

  private filterAccount(account: Account): Account | undefined {
    if (account.company_id === this.company.id && account.user_id === this.user.id) {
      return account;
    }
    return undefined;
  }
  public createAndAssign(): void {
    if (!this.user) {
      console.error('Usuario no disponible para crear batches');
      this.processing = false;
      return;
    }

    this.processing = true;

    this.assignModel.assigned.forEach((sb: SlotsBatch) => {
      if (this.assignModel.observations && this.assignModel.observations !== '') {
        sb.observations = this.assignModel.observations;
      }
      sb.reference = this.assignModel.reference;
      sb.parent_id = this.assignModel.parent_id;
      // sb.actors = this.assignModel.actors;
      sb.date = this.selectedBatch.date;
      sb.destination = this.selectedBatch.destination;
      sb.product = this.selectedBatch.product;
      sb.recipient = this.selectedBatch.recipient;
      sb.status = { id: 1 };

      if (this.selectedBatch.cpe) {
        sb.cpe = this.selectedBatch.cpe;
      }
    });

    // Obtener el account del usuario para esta compañía
    const account = this.user.accounts?.filter(this.filterAccount, this).find(acc => acc !== undefined);
    
    if (!account) {
      console.error('No se encontró una cuenta válida para el usuario y la compañía', {
        userId: this.user?.id,
        companyId: this.company?.id,
        accounts: this.user?.accounts
      });
      this.processing = false;
      this.dataDogLoggerService.error('No se encontró account válido', {
        userId: this.user?.id,
        companyId: this.company?.id
      });
      return;
    }

    this.subscriptions.push(this.slotService.create(this.company.id, account, this.assignModel.assigned, this.slotRequest).subscribe({
      next: response => {
        this.intercomService.track('slots-batch-created');
        // Save form reuse
        this.saveForm(() => this.doneModalProcessing());
      },
      error: error => {
        console.error('Error al crear batches:', error);
        this.processing = false;
        this.dataDogLoggerService.error('Error al crear batches de cupos', error);
      }
    }));
  }
  public assignableCompaniesLoaded(e: any): void {
    // TODO: Filter Companies that have already been assigned a batch
    if (this.assignModel.noMoreCompanies === undefined) this.assignModel.noMoreCompanies = (e.results && e.results.length === 0);
  }

  /**
   * Modal display methods
   *  
   */
  private execModal(template: TemplateRef<any>, batch: SlotsBatch = undefined): void {
    this.reasons = undefined;
    this.selectedBatch = batch;
    this.openModal(template, 'modal-lg');
  }

  /** Generic Modal trigger. */
  private openModal(template: TemplateRef<any>, c: string = ''): void {
    const newModal = this.modalService.show(template, { class: c });

    this.modalRef = [...(this.modalRef || []), newModal];
    newModal.onHide.subscribe(() => {
      // Removes last modal reference
      this.modalRef.pop();
    });
  }

  /** Closes the most recent opened modal. */
  public closeModal(onHide: Function = null): void {
    const mr = this.modalRef[this.modalRef.length - 1];

    if (mr) {
      mr.hide();
      if (onHide) mr.onHide.subscribe(onHide);
    } else {
      if (onHide) onHide();
    }
  }

  /** @ignore */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    this.modalRef?.forEach(modal => modal.hide());

    // Unsubscribe from everything
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
